import { useMemo } from "react";

interface UseChatWorkerQualificationParams {
  /** The qualification from the Sendbird channel metadata. */
  channelQualification?: string;
  /** The worker's own qualification from their profile. */
  workerQualification?: string;
  /** Qualifications the worker matches at this workplace. */
  matchingFacilityQualifications?: string[];
}

interface UseChatWorkerQualificationReturn {
  /**
   * The best qualification to use for the invite, following the legacy priority:
   * 1. channelQualification (if it matches workplace qualifications)
   * 2. workerQualification (if it matches)
   * 3. First matching qualification
   * 4. workerQualification as fallback
   */
  defaultQualification: string | undefined;
  /** All qualifications the worker can be invited as at this workplace. */
  availableQualifications: string[];
}

/**
 * Determines the default qualification for a chat-based shift invite.
 *
 * Replicates the legacy priority logic from useChatWorkerQualifications:
 * channel qualification > worker qualification > first match > fallback.
 */
export function useChatWorkerQualification(
  params: UseChatWorkerQualificationParams
): UseChatWorkerQualificationReturn {
  const { channelQualification, workerQualification, matchingFacilityQualifications = [] } = params;

  return useMemo(() => {
    if (matchingFacilityQualifications.length === 0) {
      return {
        defaultQualification: workerQualification,
        availableQualifications: workerQualification ? [workerQualification] : [],
      };
    }

    // Priority 1: channel's qualification if it matches workplace qualifications
    const channelMatch = matchingFacilityQualifications.find((q) => q === channelQualification);
    if (channelMatch) {
      return {
        defaultQualification: channelMatch,
        availableQualifications: matchingFacilityQualifications,
      };
    }

    // Priority 2: worker's own qualification if it matches
    const workerMatch = matchingFacilityQualifications.find((q) => q === workerQualification);
    if (workerMatch) {
      return {
        defaultQualification: workerMatch,
        availableQualifications: matchingFacilityQualifications,
      };
    }

    // Priority 3: first matching qualification
    return {
      defaultQualification: matchingFacilityQualifications[0],
      availableQualifications: matchingFacilityQualifications,
    };
  }, [channelQualification, workerQualification, matchingFacilityQualifications]);
}
